
import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { getPrayerCategories } from '@/lib/categories';
import { Button, Card } from '@/components/ui';
import { QueueItemCard } from './QueueItemCard';
import type { QueueItem, GroupedQueueItems } from '@/hooks/useProducerQueue';

export interface QueueColumnProps {
  title: string;
  queueType: 'prayer' | 'praise';
  items: QueueItem[];
  groupedItems?: GroupedQueueItems;
  isLoading: boolean;
  onRemove: (prayerId: string) => void;
  groupByCategory?: boolean;
  /** Only passed for prayer column: which prayer is currently "live" */
  livePrayerRequestId?: string | null;
  /** Only passed for prayer column: which prayer the shuffle will show next */
  shufflePrayerNextRequestId?: string | null;
  /** Only passed for prayer column: whether to show "Next in shuffle" (true when user manually displayed) */
  showNextInShuffle?: boolean;
  /** Only passed for prayer column: set this prayer as live */
  onDisplayLive?: (prayerRequestId: string) => void;
  /** Only passed for praise column: which praise is currently "live" */
  livePraiseRequestId?: string | null;
  /** Only passed for praise column: which praise the shuffle will show next */
  shufflePraiseNextRequestId?: string | null;
  /** Only passed for praise column: set this praise as live */
  onDisplayLivePraise?: (prayerRequestId: string) => void;
  /** Copy this queue to clipboard; type is this column's queue type */
  onCopyQueue?: () => void | Promise<void>;
  /** Clear displayed cards only; pending items stay in the queue */
  onClearAll?: () => void | Promise<void>;
  /** Edit prayer from card (opens editor on producer page) */
  onEdit?: (prayer: import('@/types').PrayerRequest) => void;
  /** Prayer ID -> { userId, userName } for who is currently editing (real-time) */
  editingBy?: Record<string, { userId: string; userName: string }>;
  /** Current user ID so we can allow editing when the editor is self */
  currentUserId?: string | null;
  /** When true and groupByCategory, start with all categories collapsed (e.g. for ShuffleLayout Remaining section) */
  defaultCollapsedToAll?: boolean;
}

export function QueueColumn({
  title,
  queueType,
  items,
  groupedItems,
  isLoading,
  onRemove,
  groupByCategory = false,
  livePrayerRequestId,
  shufflePrayerNextRequestId,
  showNextInShuffle = false,
  onDisplayLive,
  livePraiseRequestId,
  shufflePraiseNextRequestId,
  onDisplayLivePraise,
  onCopyQueue,
  onClearAll,
  onEdit,
  editingBy = {},
  currentUserId = null,
  defaultCollapsedToAll = false,
}: QueueColumnProps) {
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(() =>
    defaultCollapsedToAll && groupByCategory && groupedItems
      ? new Set(Object.keys(groupedItems))
      : new Set()
  );

  const toggleCategory = (category: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Count stats
  const stats = useMemo(() => {
    const total = items.length;
    const displayed = items.filter(item => item.isDisplayed).length;
    const pending = total - displayed;
    return { total, displayed, pending };
  }, [items]);

  const { pendingItems, displayedItems } = useMemo(() => {
    const pendingItems = items.filter(item => !item.isDisplayed);
    const displayedItems = items.filter(item => item.isDisplayed);
    return { pendingItems, displayedItems };
  }, [items]);

  const displayedGrouped = useMemo(() => {
    if (!groupByCategory || !groupedItems) return null;

    const grouped: Record<string, QueueItem[]> = {};
    for (const item of displayedItems) {
      const cats = getPrayerCategories(item.prayerRequest ?? {});
      const labels = cats.length > 0 ? cats : ['General'];
      for (const category of labels) {
        if (!grouped[category]) grouped[category] = [];
        grouped[category].push(item);
      }
    }
    return grouped;
  }, [groupByCategory, groupedItems, displayedItems]);

  // Loading state
  if (isLoading) {
    return (
      <Card variant="default" padding="none" className="flex flex-col h-full overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="h-7 bg-slate-200 dark:bg-slate-700 rounded animate-pulse w-1/2" />
        </div>
        <div className="flex-1 p-4 space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-slate-100 dark:bg-slate-700 rounded-lg animate-pulse" />
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card variant="default" padding="none" className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div
        className={cn(
          'p-4 border-b border-slate-200 dark:border-slate-700',
          queueType === 'prayer' ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-amber-50 dark:bg-amber-900/20'
        )}
      >
        {/* Row 1: icon + title + pending badge + actions */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
                queueType === 'prayer'
                  ? 'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400'
                  : 'bg-amber-100 dark:bg-amber-800 text-amber-600 dark:text-amber-400'
              )}
            >
              {queueType === 'prayer' ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                  />
                </svg>
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h2>
              <div className="mt-0.5 inline-flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300">
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 dark:bg-slate-800">
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      queueType === 'prayer' ? 'bg-blue-500' : 'bg-amber-500'
                    )}
                  />
                  <span>{stats.pending} pending</span>
                </span>
                <span className="text-slate-400 dark:text-slate-500">
                  {stats.total} total, {stats.displayed} shown
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onCopyQueue && (
              <Button
                variant="ghost"
                size="md"
                onClick={onCopyQueue}
                disabled={items.length === 0}
                title="Copy queue to clipboard"
                className="px-3"
                aria-label="Copy queue to clipboard"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2M8 16h8a2 2 0 002-2v-4m-6 10H8a2 2 0 01-2-2v-2m6 4h4a2 2 0 002-2v-4m-6 6l6-6"
                  />
                </svg>
              </Button>
            )}
            {onClearAll && (
              <Button
                variant="ghost"
                size="md"
                onClick={onClearAll}
                disabled={items.length === 0}
                title="Clear displayed items"
                aria-label="Clear displayed items"
                className="px-3 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Pending section */}
        <section>
          <div className="mb-3 flex items-center gap-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
              Pending
            </h3>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {pendingItems.length}
            </span>
          </div>
          {pendingItems.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500">No pending items.</p>
          ) : (
            <div className="space-y-3">
              {pendingItems.map((item, index) => (
                <QueueItemCard
                  key={item.id}
                  item={item}
                  position={index + 1}
                  queueType={queueType}
                  onRemove={onRemove}
                  livePrayerRequestId={livePrayerRequestId}
                  shufflePrayerNextRequestId={shufflePrayerNextRequestId}
                  showNextInShuffle={showNextInShuffle}
                  onDisplayLive={onDisplayLive}
                  livePraiseRequestId={livePraiseRequestId}
                  shufflePraiseNextRequestId={shufflePraiseNextRequestId}
                  onDisplayLivePraise={onDisplayLivePraise}
                  onEdit={onEdit}
                  editingBy={editingBy}
                  currentUserId={currentUserId}
                />
              ))}
            </div>
          )}
        </section>

        {/* Displayed section */}
        <section>
          <div className="mb-3 flex items-center gap-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
              Displayed
            </h3>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {displayedItems.length}
            </span>
          </div>

          {displayedItems.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500">No displayed items yet.</p>
          ) : groupByCategory && displayedGrouped ? (
            <div className="space-y-5">
              {Object.entries(displayedGrouped).map(([category, categoryItems]) => (
                <div key={category} className="space-y-3">
                  {/* Category header */}
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <svg
                      className={cn(
                        'w-4 h-4 text-slate-400 transition-transform',
                        collapsedCategories.has(category) && '-rotate-90'
                      )}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    <span className="font-medium text-slate-700 dark:text-slate-300">
                      <span
                        className={cn(
                          'inline-block w-2 h-2 rounded-full mr-2 align-middle',
                          queueType === 'prayer' ? 'bg-blue-500' : 'bg-amber-500'
                        )}
                      />
                      {category}
                    </span>
                    <span className="px-2 py-0.5 bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 rounded-full text-xs">
                      {categoryItems.length}
                    </span>
                  </button>

                  {/* Category items */}
                  {!collapsedCategories.has(category) && (
                    <div className="space-y-3 ml-6">
                      {categoryItems.map((item, index) => (
                        <QueueItemCard
                          key={item.id}
                          item={item}
                          position={index + 1}
                          queueType={queueType}
                          onRemove={onRemove}
                          livePrayerRequestId={livePrayerRequestId}
                          shufflePrayerNextRequestId={shufflePrayerNextRequestId}
                          showNextInShuffle={showNextInShuffle}
                          onDisplayLive={onDisplayLive}
                          livePraiseRequestId={livePraiseRequestId}
                          shufflePraiseNextRequestId={shufflePraiseNextRequestId}
                          onDisplayLivePraise={onDisplayLivePraise}
                          onEdit={onEdit}
                          editingBy={editingBy}
                          currentUserId={currentUserId}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {displayedItems.map((item, index) => (
                <QueueItemCard
                  key={item.id}
                  item={item}
                  position={index + 1}
                  queueType={queueType}
                  onRemove={onRemove}
                  livePrayerRequestId={livePrayerRequestId}
                  shufflePrayerNextRequestId={shufflePrayerNextRequestId}
                  showNextInShuffle={showNextInShuffle}
                  onDisplayLive={onDisplayLive}
                  livePraiseRequestId={livePraiseRequestId}
                  shufflePraiseNextRequestId={shufflePraiseNextRequestId}
                  onDisplayLivePraise={onDisplayLivePraise}
                  onEdit={onEdit}
                  editingBy={editingBy}
                  currentUserId={currentUserId}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </Card>
  );
}
